import { useEffect, useState } from 'react';
import { Footer, TopBar } from '../components/TopBar';

/** The 51 codes the server serves state pages for (mirrors src/server/routes/seo.ts). */
export const STATE_CODES = new Set(
  'al ak az ar ca co ct de dc fl ga hi id il in ia ks ky la me md ma mi mn ms mo mt ne nv nh nj nm ny nc nd oh ok or pa ri sc sd tn tx ut vt va wa wv wi wy'.split(' '),
);

/**
 * State (/va) and city (/va/roanoke) directory pages are rendered by the server for search
 * engines and first visits. Once the app loads it takes over the page, so it has to know how to
 * show them too -- otherwise the server's good page is replaced with "Page not found".
 *
 * The content is fetched from that same server page and shown inside the normal top bar and
 * footer. It is our own server's escaped HTML (the same trust as the static pages), and its links
 * are ordinary internal links the app's link handler already navigates.
 */
export function Directory({ path }: { path: string }) {
  const [page, setPage] = useState<{ title: string; html: string } | 'missing' | null>(null);

  useEffect(() => {
    let live = true;
    setPage(null);
    fetch(path + location.search, { headers: { accept: 'text/html' } })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const section = doc.querySelector('#root section');
        if (!section) throw new Error('no content');
        return { title: doc.title, html: section.innerHTML };
      })
      .then((p) => live && setPage(p))
      .catch(() => live && setPage('missing'));
    return () => {
      live = false;
    };
  }, [path]);

  useEffect(() => {
    document.title = page && page !== 'missing' ? page.title : 'The Yard Sale Map';
  }, [page]);

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        {page === 'missing' ? (
          <>
            <h1>Page not found</h1>
            <p>
              <a href="/">Back to the map</a>
            </p>
          </>
        ) : page ? (
          <article dangerouslySetInnerHTML={{ __html: page.html }} />
        ) : (
          <p aria-live="polite">Loading…</p>
        )}
      </main>
      <Footer />
    </>
  );
}
